import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import {
  ArrowLeft,
  Spinner,
  Package,
  MapPin,
  Calendar,
  FileText,
  Clock,
  Truck,
  CheckCircle,
  Buildings,
  Phone,
  Envelope,
  Money,
  CaretRight,
  CaretDown,
  CaretUp,
  ChatCircle,
  Eye,
  DownloadSimple,
  FilePdf,
  Image as ImageIcon,
  File,
  UploadSimple,
  Trash,
  X,
  Chat,
  CloudArrowUp,
  Plus,
  Star,
  ArrowsClockwise
} from '@phosphor-icons/react'
import { OrderChat } from '../../components/chat'
import { chatService } from '../../services/chat'
import { useWebSocketOptional } from '../../context/WebSocketContext'
import { DocumentViewer } from '../../components/DocumentViewer'
import { ReviewForm } from '../../components/ReviewForm'
import { consolidateBreakdown } from '../../utils/breakdownConsolidation'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { RoutingInfo } from '../../components/offer/RoutingInfo'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'
import { computeCargoMetrics } from '../../utils/cargoMetrics'
import { FEE_UNIT_MAP, computeFeeSubtotal } from '../../constants/feeUnits'
import api from '../../services/api'
import { documentService, DocumentCategory, OrderDocument as ServiceOrderDocument } from '../../services/documents'

interface TimelineEvent {
  id: string
  status: string
  title: string
  description?: string
  date: string
  user_id?: string
  user_name?: string
}

interface OrderDocument {
  id?: string
  name: string
  url: string
  type?: string
  mimeType?: string
  mime_type?: string
  source?: 'upload' | 'chat'
  category?: string
  description?: string
  uploaded_at?: string
  uploaded_by?: string
  uploaded_by_name?: string
  size?: number
  message_id?: string
}

interface Order {
  id: string
  status: string
  trackingNumber?: string
  estimatedDelivery?: string
  actualDelivery?: string
  documents?: OrderDocument[]
  timeline?: { events: TimelineEvent[] }
  createdAt: string
  updatedAt: string
  quoteId: string
  quoteTxControl: string
  origin: {
    location: { city: string; state?: string; country: string; zip_code?: string }
  }
  destination: {
    location: { city: string; state?: string; country: string; zip_code?: string }
  }
  incoterm: string
  goodsDescription?: string
  volumeItems?: Array<{
    type: string
    quantity: number
    weight: number
    dimensions?: { length: number; width: number; height: number; unit: string }
  }>
  modalityName: string
  modalityType: string
  volumeType: string
  agentName: string
  agentTradingName?: string
  agentLogoUrl?: string
  agentEmail?: string
  agentPhone?: string
  offerId: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  freeTimeDays?: number | null
  carrierName?: string | null
  routeType?: 'direct' | 'transshipment' | null
  routeLegs?: Array<{ port: string; country: string; eta?: string | null; etd?: string | null }> | null
  currency: string
  breakdown?: Record<string, number> | Array<{ id: string; name: string; value: number }>
  paymentTerms?: string
  deliveryTime?: number
  offerNotes?: string
  offerConditions?: Array<{ id: string; title: string; content: string }>
}

type TabType = 'details' | 'timeline' | 'documents' | 'chat'

export default function OrderDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showError, showSuccess, confirm } = useDialog()
  const [revalidating, setRevalidating] = useState(false)

  async function handleRequestRevalidation() {
    const ok = await confirm({
      title: 'Revalidar Frete',
      message: 'O agente contratado receberá um pedido (email + notificação) para revalidar o valor do frete antes do embarque. Deseja continuar?',
      confirmText: 'Sim, solicitar',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    setRevalidating(true)
    try {
      await api.post(`/shipper/orders/${id}/request-revalidation`)
      showSuccess('Revalidação solicitada', 'O agente foi notificado para revalidar o frete.')
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao solicitar revalidação')
    } finally {
      setRevalidating(false)
    }
  }

  const [loading, setLoading] = useState(true)
  const [order, setOrder] = useState<Order | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('details')
  const [error, setError] = useState<string | null>(null)
  const [chatUnreadCount, setChatUnreadCount] = useState(0)

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerDoc, setViewerDoc] = useState<{ url: string; name: string; type: string } | null>(null)

  // Document upload state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [categories, setCategories] = useState<DocumentCategory[]>([])
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadCategory, setUploadCategory] = useState<string>('')
  const [uploadDescription, setUploadDescription] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null)

  // State for highlighted message
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null)

  // Upload section expanded state
  const [uploadExpanded, setUploadExpanded] = useState(false)
  const [showReviewForm, setShowReviewForm] = useState(false)

  // Read tab and messageId from URL query params
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['details', 'timeline', 'documents', 'chat'].includes(tabParam)) {
      setActiveTab(tabParam as TabType)
    }
    // Read messageId for scroll-to-message feature
    const messageIdParam = searchParams.get('messageId')
    if (messageIdParam) {
      setHighlightMessageId(messageIdParam)
    }
  }, [searchParams])

  useEffect(() => {
    fetchOrderData()
    fetchCategories()
  }, [id])

  // Chat tab badge (Plane #7): seed the unread count from the server so the tab
  // shows pending messages on arrival. Skip when already on the chat tab, since
  // OrderChat marks them read and resets the count.
  useEffect(() => {
    if (!order?.id || activeTab === 'chat') return
    chatService.getUnreadCount(order.id)
      .then((r) => setChatUnreadCount(r.unreadCount))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id])

  // Realtime: bump the Chat tab badge when a new message arrives while the user
  // is on another tab (Plane #7). On the chat tab OrderChat owns the live feed.
  const ws = useWebSocketOptional()
  useEffect(() => {
    if (!ws || !order?.id) return
    const unsubscribe = ws.onNotification((n) => {
      if (
        n.type === 'new_chat_message' &&
        typeof n.reference_id === 'string' &&
        n.reference_id.startsWith(order.id) &&
        activeTab !== 'chat'
      ) {
        setChatUnreadCount((c) => c + 1)
      }
    })
    return unsubscribe
  }, [ws, order?.id, activeTab])

  async function fetchOrderData() {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/shipper/orders/${id}`)
      setOrder(response.data.order)
    } catch (err: any) {
      console.error('Error fetching order:', err)
      setError(err.response?.data?.error || 'Erro ao carregar pedido')
    } finally {
      setLoading(false)
    }
  }

  async function fetchCategories() {
    try {
      const response = await documentService.getCategories()
      setCategories(response.categories)
    } catch (err) {
      console.error('Error fetching categories:', err)
    }
  }

  async function handleUploadDocument() {
    if (!uploadFile || !id) return

    try {
      setUploading(true)
      setUploadError(null)
      await documentService.uploadDocument(id, uploadFile, uploadCategory, uploadDescription)

      // Reset form
      setUploadFile(null)
      setUploadCategory('')
      setUploadDescription('')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Refresh order data to get updated documents
      await fetchOrderData()
    } catch (err: any) {
      console.error('Error uploading document:', err)
      setUploadError(err.message || 'Erro ao enviar documento')
    } finally {
      setUploading(false)
    }
  }

  async function handleDeleteDocument(documentId: string) {
    if (!id || !documentId) return

    try {
      setDeletingDocId(documentId)
      await documentService.deleteDocument(id, documentId)
      await fetchOrderData()
    } catch (err: any) {
      console.error('Error deleting document:', err)
      showError('Erro', err.message || 'Erro ao excluir documento')
    } finally {
      setDeletingDocId(null)
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setUploadFile(file)
      setUploadError(null)
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  function getCategoryLabel(value: string): string {
    const cat = categories.find(c => c.value === value)
    return cat?.label || value
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      pending: { label: 'Aguardando Embarque', color: 'bg-amber-100 text-amber-700' },
      confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700' },
      booking: { label: 'Reserva em Processamento', color: 'bg-blue-100 text-blue-700' },
      pickup: { label: 'Aguardando Coleta', color: 'bg-orange-100 text-orange-700' },
      departed: { label: 'Embarcado', color: 'bg-indigo-100 text-indigo-700' },
      in_transit: { label: 'Embarcado / Em Trânsito', color: 'bg-purple-100 text-purple-700' },
      arrived: { label: 'Chegado no Destino', color: 'bg-teal-100 text-teal-700' },
      customs: { label: 'Desembaraco Aduaneiro', color: 'bg-cyan-100 text-cyan-700' },
      delivered: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
      canceled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
    }
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value)
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  // Check if file is an audio file (by extension or mimeType)
  const isAudioFile = (doc: OrderDocument): boolean => {
    const mimeType = doc.mimeType || doc.mime_type || ''
    if (mimeType.startsWith('audio/')) return true
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.webm', '.m4a', '.aac']
    const lowerName = doc.name.toLowerCase()
    return audioExtensions.some(ext => lowerName.endsWith(ext))
  }

  // Check if file can be previewed
  const canPreview = (doc: OrderDocument): boolean => {
    const mimeType = doc.mimeType || doc.mime_type || ''
    const lowerName = doc.name.toLowerCase()

    // PDF
    if (mimeType === 'application/pdf' || lowerName.endsWith('.pdf')) return true
    // Images
    if (mimeType.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lowerName.endsWith(ext))) return true
    // Word documents
    if (mimeType.includes('word') || mimeType.includes('document') || lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return true

    return false
  }

  // Get appropriate icon for document
  const getDocumentIcon = (doc: OrderDocument) => {
    const mimeType = doc.mimeType || doc.mime_type || ''
    const lowerName = doc.name.toLowerCase()

    if (mimeType === 'application/pdf' || lowerName.endsWith('.pdf')) {
      return <FilePdf size={24} className="text-red-500" />
    }
    if (mimeType.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lowerName.endsWith(ext))) {
      return <ImageIcon size={24} className="text-blue-500" />
    }
    return <File size={24} className="text-gray-400" />
  }

  // Open document viewer
  const openViewer = (doc: OrderDocument) => {
    // Infer mimeType from extension if not provided
    let mimeType = doc.mimeType || doc.mime_type || ''
    if (!mimeType) {
      const lowerName = doc.name.toLowerCase()
      if (lowerName.endsWith('.pdf')) mimeType = 'application/pdf'
      else if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) mimeType = 'image/jpeg'
      else if (lowerName.endsWith('.png')) mimeType = 'image/png'
      else if (lowerName.endsWith('.gif')) mimeType = 'image/gif'
      else if (lowerName.endsWith('.webp')) mimeType = 'image/webp'
      else if (lowerName.endsWith('.docx')) mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      else if (lowerName.endsWith('.doc')) mimeType = 'application/msword'
    }
    setViewerDoc({ url: doc.url, name: doc.name, type: mimeType })
    setViewerOpen(true)
  }

  // Filter out audio files from documents
  const filteredDocuments = order?.documents?.filter(doc => !isAudioFile(doc)) || []

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando pedido...</span>
        </div>
      </AppLayout>
    )
  }

  if (error || !order) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Pedido nao encontrado'}</p>
            <Button variant="outline" onClick={() => navigate('/shipper/dashboard')}>
              <ArrowLeft size={16} className="mr-2" />
              Voltar para Dashboard
            </Button>
          </div>
        </div>
      </AppLayout>
    )
  }

  const timelineEvents = order.timeline?.events || []

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/shipper/dashboard')}
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">
                    Pedido #{order.quoteTxControl}
                  </h1>
                  {getStatusBadge(order.status)}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {order.modalityName} - {order.modalityType === 'import' ? 'Importacao' : 'Exportacao'} - {order.incoterm}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {order.status === 'delivered' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReviewForm(true)}
                  className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Star size={16} weight="fill" />
                  Avaliar agente
                </Button>
              )}
              {order.status === 'pending' && (
                <Button
                  variant="outline"
                  onClick={handleRequestRevalidation}
                  disabled={revalidating}
                  className="gap-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                >
                  <ArrowsClockwise size={16} />
                  {revalidating ? 'Solicitando...' : 'Revalidar Frete'}
                </Button>
              )}
              {order.trackingNumber && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Rastreio</p>
                  <p className="font-mono font-semibold text-gray-900">{order.trackingNumber}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {showReviewForm && (
        <ReviewForm
          orderId={order.id}
          freightForwarderName={order.agentTradingName || order.agentName}
          onClose={() => setShowReviewForm(false)}
          onSubmitted={() => { setShowReviewForm(false); /* could refetch */ }}
        />
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'details', label: 'Detalhes', icon: Package },
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'documents', label: 'Documentos', icon: FileText },
              { id: 'chat', label: 'Chat', icon: ChatCircle, badge: chatUnreadCount },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Route & Cargo */}
            <div className="lg:col-span-2 space-y-6">
              {/* Route Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Truck size={20} className="text-emerald-600" />
                  Rota
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Origin */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={18} className="text-emerald-600" />
                      <span className="text-sm font-medium text-gray-500">Origem</span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {order.origin.location.city}, {order.origin.location.country}
                    </p>
                    {order.origin.location.state && (
                      <p className="text-sm text-gray-500">{order.origin.location.state}</p>
                    )}
                  </div>

                  {/* Destination */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={18} className="text-red-500" />
                      <span className="text-sm font-medium text-gray-500">Destino</span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {order.destination.location.city}, {order.destination.location.country}
                    </p>
                    {order.destination.location.state && (
                      <p className="text-sm text-gray-500">{order.destination.location.state}</p>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500">Criado em</p>
                    <p className="text-sm font-medium">{new Date(order.createdAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Previsao Entrega</p>
                    <p className="text-sm font-medium">
                      {order.estimatedDelivery
                        ? new Date(order.estimatedDelivery).toLocaleDateString('pt-BR')
                        : order.status === 'pending' ? 'Aguardando embarque' : '-'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Entrega Real</p>
                    <p className="text-sm font-medium">
                      {order.actualDelivery
                        ? new Date(order.actualDelivery).toLocaleDateString('pt-BR')
                        : '-'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tempo de Trânsito</p>
                    <p className="text-sm font-medium">
                      {order.deliveryTime ? `${order.deliveryTime} dias` : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cargo Card */}
              {order.volumeItems && order.volumeItems.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package size={20} className="text-blue-600" />
                    Carga
                  </h3>
                  {order.goodsDescription && (
                    <p className="text-sm text-gray-600 mb-4">{order.goodsDescription}</p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 font-medium text-gray-500">Tipo</th>
                          <th className="text-left py-2 font-medium text-gray-500">Qtd</th>
                          <th className="text-left py-2 font-medium text-gray-500">Peso</th>
                          <th className="text-left py-2 font-medium text-gray-500">Dimensoes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.volumeItems.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-50">
                            <td className="py-2">{item.type}</td>
                            <td className="py-2">{item.quantity}</td>
                            <td className="py-2">{item.weight} kg</td>
                            <td className="py-2">
                              {item.dimensions
                                ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}`
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Value Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Money size={20} className="text-emerald-600" />
                  Valores
                </h3>
                <div className="flex items-start justify-between mb-4">
                  <span className="text-gray-600">Valor Total</span>
                  <MultiCurrencyTotal
                    totalsByCurrency={totalsFromLegacyOffer(order)}
                    variant="compact"
                  />
                </div>
                {order.breakdown && (Array.isArray(order.breakdown) ? order.breakdown.length > 0 : Object.keys(order.breakdown).length > 0) && (
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    {Array.isArray(order.breakdown)
                      ? (() => {
                          const cargo = computeCargoMetrics(order.volumeItems as any, order.volumeType)
                          return consolidateBreakdown(order.breakdown as any).map((item, idx) => {
                            const unit = (item as any).unit || 'FORFAIT'
                            const def = FEE_UNIT_MAP[unit as keyof typeof FEE_UNIT_MAP]
                            const subtotal = def ? computeFeeSubtotal(item.value, unit, cargo) : item.value
                            const itemCurrency = item.currency || order.currency
                            return (
                              <div key={`${item.id}-${idx}`} className="flex justify-between text-sm gap-2">
                                <span className="text-gray-500 truncate">
                                  {item.name}
                                  {def && unit !== 'FORFAIT' && (
                                    <span className="text-xs text-gray-400 ml-1">
                                      ({formatCurrency(item.value, itemCurrency)}{def.shortLabel})
                                    </span>
                                  )}
                                </span>
                                <span className="font-medium whitespace-nowrap">{formatCurrency(subtotal, itemCurrency)}</span>
                              </div>
                            )
                          })
                        })()
                      : Object.entries(order.breakdown).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">{formatCurrency(value as number, order.currency)}</span>
                          </div>
                        ))
                    }
                  </div>
                )}
                {(order.carrierName || order.freeTimeDays != null || order.routeType) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <RoutingInfo
                      freeTimeDays={order.freeTimeDays}
                      carrierName={order.carrierName}
                      routeType={order.routeType}
                      routeLegs={order.routeLegs}
                    />
                  </div>
                )}
                {order.paymentTerms && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm">
                      <span className="text-gray-500">Condicoes de Pagamento:</span>{' '}
                      <span className="font-medium">{order.paymentTerms}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Agent Info */}
            <div className="space-y-6">
              {/* Agent Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Buildings size={20} className="text-blue-600" />
                  Agente de Carga
                </h3>
                <div className="flex items-center gap-4 mb-4">
                  {order.agentLogoUrl ? (
                    <img
                      src={order.agentLogoUrl}
                      alt={order.agentName}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                      {getInitials(order.agentTradingName || order.agentName)}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{order.agentTradingName || order.agentName}</p>
                    {order.agentTradingName && (
                      <p className="text-sm text-gray-500">{order.agentName}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {order.agentEmail && (
                    <a
                      href={`mailto:${order.agentEmail}`}
                      className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Envelope size={16} />
                      {order.agentEmail}
                    </a>
                  )}
                  {order.agentPhone && (
                    <a
                      href={`tel:${order.agentPhone}`}
                      className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Phone size={16} />
                      {order.agentPhone}
                    </a>
                  )}
                </div>
              </div>

              {/* Quick Status */}
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle size={24} weight="fill" />
                  <span className="font-semibold">Status do Pedido</span>
                </div>
                <p className="text-2xl font-bold mb-2">
                  {getStatusBadge(order.status).props.children}
                </p>
                <p className="text-emerald-100 text-sm">
                  Atualizado em {new Date(order.updatedAt).toLocaleDateString('pt-BR')}
                </p>
              </div>

              {/* Link to Quote */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/shipper/quotes/${order.quoteId}`)}
              >
                <FileText size={16} className="mr-2" />
                Ver Cotacao Original
              </Button>
            </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {timelineEvents.length > 0 ? (
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                {/* Events */}
                <div className="space-y-6">
                  {timelineEvents.map((event, idx) => (
                    <div key={event.id || idx} className="relative pl-10">
                      {/* Dot */}
                      <div
                        className={`absolute left-2 w-5 h-5 rounded-full border-2 ${
                          idx === 0
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'bg-white border-gray-300'
                        }`}
                      />

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                          <span className="text-xs text-gray-500">
                            {new Date(event.date).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-gray-600">{event.description}</p>
                        )}
                        {event.user_name && (
                          <p className="text-xs text-gray-400 mt-2">por {event.user_name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhum evento registrado ainda</p>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* Upload Form - Collapsible */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setUploadExpanded(!uploadExpanded)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Plus size={20} className="text-emerald-600" />
                  <span className="font-semibold text-gray-900">Enviar Documento</span>
                </div>
                {uploadExpanded ? (
                  <CaretUp size={20} className="text-gray-400" />
                ) : (
                  <CaretDown size={20} className="text-gray-400" />
                )}
              </button>

              {uploadExpanded && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-100 space-y-4">
                  {/* File Input */}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
                      className="hidden"
                    />
                    {uploadFile ? (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        {getDocumentIcon({ name: uploadFile.name, url: '', mimeType: uploadFile.type })}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{uploadFile.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(uploadFile.size)}</p>
                        </div>
                        <button
                          onClick={() => {
                            setUploadFile(null)
                            if (fileInputRef.current) fileInputRef.current.value = ''
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors flex flex-col items-center gap-2"
                      >
                        <UploadSimple size={32} className="text-gray-400" />
                        <span className="text-sm text-gray-600">Clique para selecionar um arquivo</span>
                        <span className="text-xs text-gray-400">PDF, DOC, XLS, Imagens ou ZIP (max 20MB)</span>
                      </button>
                    )}
                  </div>

                  {/* Category Select */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Documento
                      </label>
                      <select
                        value={uploadCategory}
                        onChange={(e) => setUploadCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">Selecione o tipo...</option>
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descricao (opcional)
                      </label>
                      <input
                        type="text"
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        placeholder="Ex: Fatura comercial #12345"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {uploadError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      {uploadError}
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleUploadDocument}
                      disabled={!uploadFile || uploading}
                      className="gap-2"
                    >
                      {uploading ? (
                        <>
                          <Spinner size={16} className="animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <UploadSimple size={16} />
                          Enviar Documento
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Documents List */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                Documentos ({filteredDocuments.length})
              </h3>

              {filteredDocuments.length > 0 ? (
                <div className="space-y-3">
                  {filteredDocuments.map((doc, idx) => (
                    <div
                      key={doc.id || idx}
                      className="flex items-start gap-3 p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getDocumentIcon(doc)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900 truncate">{doc.name}</span>
                          {doc.source === 'chat' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                              <Chat size={12} />
                              Chat
                            </span>
                          ) : doc.source === 'upload' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              <CloudArrowUp size={12} />
                              Upload
                            </span>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {doc.category && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              {getCategoryLabel(doc.category)}
                            </span>
                          )}
                          {doc.size && (
                            <span>{formatFileSize(doc.size)}</span>
                          )}
                          {doc.uploaded_at && (
                            <span>
                              {new Date(doc.uploaded_at).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          )}
                          {doc.uploaded_by_name && (
                            <span>por {doc.uploaded_by_name}</span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {canPreview(doc) && (
                          <button
                            onClick={() => openViewer(doc)}
                            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Visualizar"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                        {doc.source === 'chat' && doc.message_id && (
                          <button
                            onClick={() => {
                              setActiveTab('chat')
                              setHighlightMessageId(doc.message_id!)
                            }}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Ver no chat"
                          >
                            <Chat size={18} />
                          </button>
                        )}
                        <a
                          href={doc.url}
                          download={doc.name}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Baixar"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DownloadSimple size={18} />
                        </a>
                        {doc.id && doc.source === 'upload' && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id!)}
                            disabled={deletingDocId === doc.id}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Excluir"
                          >
                            {deletingDocId === doc.id ? (
                              <Spinner size={18} className="animate-spin" />
                            ) : (
                              <Trash size={18} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Nenhum documento anexado</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Envie documentos usando o formulario acima ou pelo chat
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ChatCircle size={24} className="text-emerald-600" />
                Conversa do Pedido
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Converse com o {order.agentTradingName || order.agentName} sobre este pedido.
                Arquivos enviados serao automaticamente adicionados aos documentos.
              </p>
            </div>
            <OrderChat
              orderId={order.id}
              orderSummary={{
                txControl: order.quoteTxControl,
                status: order.status,
                modalityName: order.modalityName,
                incoterm: order.incoterm,
                origin: {
                  city: order.origin?.location?.city || '',
                  country: order.origin?.location?.country || ''
                },
                destination: {
                  city: order.destination?.location?.city || '',
                  country: order.destination?.location?.country || ''
                },
                totalValue: order.totalValue,
                totalsByCurrency: order.totalsByCurrency,
                currency: order.currency,
                estimatedDelivery: order.estimatedDelivery
              }}
              onUnreadCountChange={setChatUnreadCount}
              highlightMessageId={highlightMessageId}
            />
          </div>
        )}
      </main>

      {/* Document Viewer Modal */}
      {viewerDoc && (
        <DocumentViewer
          isOpen={viewerOpen}
          onClose={() => {
            setViewerOpen(false)
            setViewerDoc(null)
          }}
          url={viewerDoc.url}
          fileName={viewerDoc.name}
          mimeType={viewerDoc.type}
        />
      )}
    </AppLayout>
  )
}
